import React from "react";
import { Navigate } from "react-router-dom";

import RootLanding from "./root/RootLanding";
import RootSegmentDispatcher from "./root/RootSegmentDispatcher";

// import HomepageLayout from "./index"; // TODO: トップページ完成後に復帰
import Os from "./os/Os";
import MyPage from "./my-page/MyPage";
import MyProfile from "./my-profile/MyProfile";
import Gallery from "./gallery/Gallery";
import ProjectInfo from "./project-settings/ProjectInfo";
import ProjectConfig from "./project-settings/ProjectConfig";
import ProjectCreation from "./project-settings/ProjectCreation";
// Home, TalkRoom, DM は ProjectWrapper 内で Activity 管理（RootSegmentDispatcher 参照）
import Projects from "./projects/Projects";
import TaskBoardPage from "./taskboard/TaskBoardPage";
import Notes from "./notes/Notes";
import Library from "./library/Library";
import Tools from "./tools/Tools";
import PhraseBloom from "./tools/phrase-bloom/PhraseBloom";
import ReLocate from "./tools/re-locate/ReLocate";
import RendaK from "./tools/renda-k/RendaK";
import StepEstimate from "./tools/step-estimate/StepEstimate";
import Preview from "./preview/Preview";
import FreeSoundAssets from "./free-sound-assets/2025-summer";
import FreeSoundAssets2026Winter from "./free-sound-assets/2026-winter";
import Careers from "./careers/Careers";
import Contact from "./contact/Contact";
import About from "./about/About";
import LegalPage from "./legal/LegalPage";
import UpdateInfoRedirect from "./updateinfo/UpdateInfoRedirect";
import Mgk from "./mgk/Mgk";
import Roles from "./roles/Roles";
import Payment from "./payment/Payment";
import Account from "./account/Account";
import AdminBalanceCredit from "./admin-balance/AdminBalanceCredit";
import AdminAcrResults from "./admin-acr/AdminAcrResults";
import AdminAiSettings from "./admin-ai/AdminAiSettings";
import Profile from "./profile/Profile";
import SharePage from "./share/SharePage";
import DemoEntry from "./demo/DemoEntry";
import AdminDemoLinks from "./admin-demo/AdminDemoLinks";
import AdminGalleryTags from "./admin-gallery-tags/AdminGalleryTags";
import AdminProjects from "./admin-projects/AdminProjects";
import AdminMileageRanks from "./admin-mileage/AdminMileageRanks";
import AdminGitHubRepositories from "./admin-github/AdminGitHubRepositories";

/** ルートスキーマの型定義。App.tsx の <Routes> 生成で共有する。 */
export type RouteSchema = {
  path: string;
  component: React.ReactNode;
  /** GlobalHeader を隠すか（全画面ページ用） */
  isHideTopBar?: boolean;
  /** 認証必須ルート。未サインイン時はサインインへリダイレクトする */
  requireAuth?: boolean;
  /**
   * GlobalWrapper / RequireAuth の自動ラップをスキップする。
   * ケースによって認証要否・ヘッダー表示が変わるルート
   * （RootSegmentDispatcher）はコンポーネント側で自前で巻く。
   */
  raw?: boolean;
  children?: RouteSchema[];
};

const routes: RouteSchema[] = [
  {
    // サイトトップ（soremo.jp/）: 未ログインはランディングプロフィール、
    // ログイン済みはプロジェクト一覧へ（RootLanding 参照）。
    path: "/",
    component: <RootLanding />,
    isHideTopBar: true,
  },
  {
    path: "/projects",
    component: <Projects />,
    isHideTopBar: false,
    requireAuth: true,
  },
  {
    // 横断タスクボードのスタンドアロン表示。リテラルパスのため
    // キャッチオール `/:projectId` より前に置く。
    path: "/taskboard",
    component: <TaskBoardPage />,
    isHideTopBar: false,
    requireAuth: true,
  },
  {
    // タスクボードのアクティビティ集計の旧 URL。横断タスクボードのビューへ
    // 統合されたため、新 URL（`?view=activity`）へリダイレクトする。
    path: "/taskboard/activity",
    component: <Navigate to="/taskboard?view=activity" replace />,
    isHideTopBar: false,
    requireAuth: true,
  },
  {
    // ノート（Notion/Obsidian 風の Markdown 情報集約ツール）のスタンドアロン
    // 表示。グローバルメニューの「共有ノート」から遷移し、参加権限のある
    // 全ノートを横断表示する（`?project=<id>` で絞り込みの直リンクも可）。
    // プロジェクト内のノートアイコンはここではなく `/:projectId?tab=notes`
    // の埋め込み表示（ProjectWrapper）に遷移する。ノートへの直リンクは
    // `/notes/:noteId`。リテラルパスのためキャッチオール `/:projectId` より
    // 前に置く。
    // requireAuth なし: is_public セクション（ヘルプセンター等の外部公開）を
    // 非ログインでも閲覧できるようにするため。未ログイン時に何が見えるかは
    // BE のセクション権限（public ロール）が決める。
    path: "/notes",
    component: <Notes />,
    isHideTopBar: false,
    requireAuth: false,
  },
  {
    path: "/notes/:noteId",
    component: <Notes />,
    isHideTopBar: false,
    requireAuth: false,
  },
  {
    // 共有ライブラリ（ファイルストレージ）のスタンドアロン表示。
    // グローバルメニューの「共有ライブラリ」から遷移し、参加権限のある
    // 全ライブラリを横断表示する。プロジェクト内のストレージアイコンは
    // ここではなく `/:projectId?tab=library` の埋め込み表示（ProjectWrapper）
    // に遷移する。リテラルパスのためキャッチオール `/:projectId` より前に置く。
    // 共有ノートと異なり公開（匿名閲覧）は無いため requireAuth。
    path: "/library",
    component: <Library />,
    isHideTopBar: false,
    requireAuth: true,
  },
  {
    // 実験的ツール群のハブページ。グローバルメニュー（CREATOR / CLIENT）の
    // TOOL から遷移する。リテラルパスのためキャッチオール `/:projectId` より
    // 前に置く。
    path: "/tool",
    component: <Tools />,
    isHideTopBar: false,
    requireAuth: true,
  },
  {
    // 旧 URL（/tools）からのリダイレクト。リダイレクトしないと
    // キャッチオール `/:projectId` に吸われて壊れたプロジェクト画面になる。
    path: "/tools",
    component: <Navigate to="/tool" replace />,
  },
  {
    // 上昇/下降フレーズ特化の MIDI + オーディオ生成ツール（実験的機能）。
    // 2 セグメントのためキャッチオール `/:projectId` とは衝突しない。
    path: "/tool/phrase-bloom",
    component: <PhraseBloom />,
    isHideTopBar: false,
    requireAuth: true,
  },
  {
    // 読み込んだオーディオの指定位置を BPM 同期で連打するスタッター
    // エフェクトツール（実験的機能）。2 セグメントのためキャッチオール
    // `/:projectId` とは衝突しない。
    path: "/tool/renda-k",
    component: <RendaK />,
    isHideTopBar: false,
    requireAuth: true,
  },
  {
    // 項目の選択肢表からステップナビゲーションで見積もりを組み立て、
    // 御見積書 PDF を出力するツール「ステップ見積もり -超松竹梅-」
    // （実験的機能）。2 セグメントのためキャッチオール `/:projectId` とは
    // 衝突しない。
    path: "/tool/step-estimate",
    component: <StepEstimate />,
    isHideTopBar: false,
    requireAuth: true,
  },
  {
    // Nuendo のプレビュー用 XML（ビデオ + マーカートラック）を REAPER の RPP へ
    // 移植するツール「RE:LOCATE」（実験的機能）。2 セグメントのためキャッチ
    // オール `/:projectId` とは衝突しない。
    path: "/tool/re-locate",
    component: <ReLocate />,
    isHideTopBar: false,
    requireAuth: true,
  },
  {
    path: "/preview",
    component: <Preview />,
    isHideTopBar: false,
    requireAuth: true,
  },
  {
    path: "/os",
    component: <Os />,
    isHideTopBar: true,
  },
  {
    path: "/careers",
    component: <Careers />,
    isHideTopBar: true,
  },
  {
    path: "/contact",
    component: <Contact />,
    isHideTopBar: true,
  },
  {
    // 旧 Django SSR の公開静的ページ（会社概要 / 法務）。SPA 移植後は
    // CloudFront のビヘイビアを外し、ここで受ける（reservedSlugs.ts にも登録済み）。
    path: "/about",
    component: <About />,
    isHideTopBar: true,
  },
  {
    path: "/privacypolicy",
    component: <LegalPage document="privacy-policy" />,
    isHideTopBar: true,
  },
  {
    path: "/termsofservice",
    component: <LegalPage document="terms-of-service" />,
    isHideTopBar: true,
  },
  {
    path: "/tokushoho",
    component: <LegalPage document="tokushoho" />,
    isHideTopBar: true,
  },
  {
    // MUSIC GATE KEEPER（類似曲著作権リサーチ）の LP + お申し込みフロー
    path: "/mgk",
    component: <Mgk />,
    isHideTopBar: true,
  },
  {
    // アップデート情報は共有ノート（公開セクション）へ移行。旧 URL は転送のみ
    path: "/updateinfo",
    component: <UpdateInfoRedirect />,
    isHideTopBar: true,
  },
  {
    path: "/free-sound-assets",
    component: <FreeSoundAssets />,
    isHideTopBar: true,
  },
  {
    path: "/free-sound-assets/2026-winter",
    component: <FreeSoundAssets2026Winter />,
    isHideTopBar: true,
  },
  {
    path: "/my-page",
    component: <MyPage />,
    isHideTopBar: false,
    requireAuth: true,
  },
  {
    path: "/gallery",
    component: <Gallery />,
    isHideTopBar: false,
    requireAuth: true,
  },
  {
    path: "/my-profile",
    component: <MyProfile />,
    isHideTopBar: false,
    requireAuth: true,
  },
  {
    path: "/roles",
    component: <Roles />,
    isHideTopBar: false,
    requireAuth: true,
  },
  {
    path: "/payment",
    component: <Payment />,
    isHideTopBar: false,
    requireAuth: true,
  },
  {
    // アカウント管理ページ（ADMIN = ロール別タブ / MANAGER = クリエイター一覧）。
    // GlobalHeader の ACCOUNT メニューから遷移する。リテラルパスのため
    // キャッチオール `/:projectId` より前に置く。ページ内側でも role ガード
    // し、未権限ユーザーは `/projects` へフォールバックする。
    path: "/account",
    component: <Account />,
    isHideTopBar: false,
    requireAuth: true,
  },
  {
    // 管理者（ADMIN = master_admin）専用: 残高付与ページ。
    // 旧パスは /admin/* だったが、ルート直下配信化に伴い Django 管理画面
    // （soremo.jp/admin/ → ALB）とプレフィックスが衝突するため /console/* に
    // 移設した。2 セグメントのためキャッチオール `/:projectId` とは衝突しない。
    // ページ内側でも role ガードしているが、`/projects` へのフォールバックで
    // 未権限ユーザーには見えないようにする。
    path: "/console/balance-credit",
    component: <AdminBalanceCredit />,
    isHideTopBar: false,
    requireAuth: true,
  },
  {
    // 管理者専用: ACR Cloud 結果一覧ページ。プロジェクトを選択して、
    // ショット内メディア / ショットコメント添付 / トークルーム添付の
    // ACR スキャン結果を一覧表示し、CSV クリップボードコピー / 照合中の
    // 一括再スキャンを行う。`/console/balance-credit` と同じく 2 セグメント
    // のためキャッチオール `/:projectId` とは衝突しない。
    path: "/console/acr",
    component: <AdminAcrResults />,
    isHideTopBar: false,
    requireAuth: true,
  },
  {
    // 管理者専用: AI 設定ページ。AI モデル設定（プロバイダ/モデル/パラメータ）
    // とシーン別プロンプトを名前付きプリセットとして複数保存し、アクティブな
    // ものを切り替える。`/console/acr` と同じく 2 セグメントのためキャッチオール
    // `/:projectId` とは衝突しない。
    path: "/console/ai",
    component: <AdminAiSettings />,
    isHideTopBar: false,
    requireAuth: true,
  },
  {
    // 管理者専用: デモリンク管理ページ。プロジェクトとメンバーを指定して
    // 外部公開向けデモリンク（/demo/:token）を発行・失効する。ページ内で
    // ADMIN ロールを検証し、未権限ユーザーは `/projects` へフォールバック
    // する。2 セグメントのためキャッチオール `/:projectId` とは衝突しない。
    path: "/console/demo",
    component: <AdminDemoLinks />,
    isHideTopBar: false,
    requireAuth: true,
  },
  {
    // 管理者・マネージャー専用: GALLERY タグ統合（マスタータグ＋エイリアス）
    // 管理ページ。同義タグをマスタータグに束ね、GALLERY の検索・タグ候補で
    // 同一視する。ページ内で ADMIN / MANAGER ロールを検証し、未権限ユーザーは
    // `/projects` へフォールバックする。2 セグメントのためキャッチオール
    // `/:projectId` とは衝突しない。
    path: "/console/gallery-tags",
    component: <AdminGalleryTags />,
    isHideTopBar: false,
    requireAuth: true,
  },
  {
    // 管理者専用: プロジェクト管理ページ。全プロジェクトの集計列付き一覧
    // （メンバー・ショット・メッセージ・DM スレッド・総費用）と削除を行う。
    // ページ内で ADMIN ロールを検証し、未権限ユーザーは `/projects` へ
    // フォールバックする。2 セグメントのためキャッチオール `/:projectId`
    // とは衝突しない。
    path: "/console/projects",
    component: <AdminProjects />,
    isHideTopBar: false,
    requireAuth: true,
  },
  {
    // 管理者専用: マイレージ編集ページ。旧 Django `/mileages/` 画面の移植で、
    // マイレージランク（ステージ）定義の CRUD を行う。ページ内で ADMIN
    // ロールを検証し、未権限ユーザーは `/projects` へフォールバックする。
    // 2 セグメントのためキャッチオール `/:projectId` とは衝突しない。
    path: "/console/mileage",
    component: <AdminMileageRanks />,
    isHideTopBar: false,
    requireAuth: true,
  },
  {
    // 管理者専用: GitHub リポジトリ管理ページ。soremon から操作するリポジトリを
    // 登録し、プロジェクトへの紐付けと閲覧・書き込み権限を設定する。ページ内で
    // ADMIN ロールを検証し、未権限ユーザーは `/projects` へフォールバックする。
    // 2 セグメントのためキャッチオール `/:projectId` とは衝突しない。
    path: "/console/github",
    component: <AdminGitHubRepositories />,
    isHideTopBar: false,
    requireAuth: true,
  },
  {
    path: "/project-info",
    component: <ProjectInfo />,
    isHideTopBar: true,
    requireAuth: true,
  },
  {
    path: "/project-config",
    component: <ProjectConfig />,
    isHideTopBar: true,
    requireAuth: true,
  },
  {
    path: "/project-creation",
    component: <ProjectCreation />,
    isHideTopBar: true,
    requireAuth: true,
  },
  {
    // クリエイター公開プロフィールの明示ルート。公開 URL は `soremo.jp/{slug}`
    // （RootSegmentDispatcher が担当）だが、本人プレビューや内部リンク用に
    // `/profile/{slug}` でも開けるようにする。リテラル "profile" 始まりのため
    // キャッチオール `/:projectId` とは衝突しない（公開ページなので
    // requireAuth は付けない）。
    path: "/profile/:slug",
    component: <Profile />,
    isHideTopBar: true,
    requireAuth: false,
  },
  {
    // トークルームのファイル一覧から発行された共有リンクのランディングページ。
    // アクセス制限なしのリンクは未サインインでも開けるため requireAuth は付けない
    // （制限付きリンクはページ内で 401/403 を表示してサインインへ誘導する）。
    // リテラル "share" 始まりのためキャッチオール `/:projectId` とは衝突しない。
    path: "/share/:token",
    component: <SharePage />,
    isHideTopBar: false,
    requireAuth: false,
  },
  {
    // デモモード（外部公開向けサンプルサイト）の入口。管理者が /console/demo で
    // 発行したリンクを開くと、デモ閲覧アカウントの認証 Cookie をセットして
    // プロジェクト画面へ遷移する。サインインなしで開くため requireAuth は
    // 付けない。リテラル "demo" 始まりのためキャッチオール `/:projectId` とは
    // 衝突しない。
    path: "/demo/:token",
    component: <DemoEntry />,
    isHideTopBar: true,
    requireAuth: false,
  },
  {
    // ルート直下の 1 セグメント: UUID ならプロジェクト詳細、slug 形式なら
    // 公開プロフィール（RootSegmentDispatcher 参照）。認証要否・ヘッダー表示が
    // ケースで変わるため raw 指定（RequireAuth / GlobalWrapper は内側で巻く）。
    // パラメータ名は ProjectWrapper が useParams().projectId を読むため維持。
    path: "/:projectId",
    component: <RootSegmentDispatcher />,
    raw: true,
    children: [
      {
        // Preview のみルーティングで残す（他は ProjectWrapper 内で Activity 管理）
        path: ":shotId",
        component: <Preview />,
      },
    ],
  },
  {
    // どのルートにも一致しない多段パスはサインインへ退避する。
    path: "*",
    component: <Navigate to="/signin" replace />,
    raw: true,
  },
  // ...TopRouteSchema,
];

export default routes;
